import { useMemo } from "react";
import type { Audit, AuditScope } from "@/services/api/guardiansv2/types";
import { AuditPersonaTabs, computeTabVisualState, type AuditPersonaTabId } from "./AuditPersonaTabs";
import { PersonaPanel } from "./PersonaPanel";
import { resolveSynthesisBody } from "./auditPresentation";
import { entityTextScopeFromAuditScope } from "../shared/entityTags";
import { EntityRichText } from "../shared/EntityRichText";
import type { ScopeLabelCache } from "./useScopeLabelCache";
import { isAuditPending } from "../auditLaunch/auditPending";

function placeholderAudit(auditId: string, scope: AuditScope): Audit {
  const now = new Date().toISOString();
  return {
    _id: auditId,
    client_id: "",
    audit_type: "task_complete",
    subject: { type: "task", id: scope.task_id ?? "" },
    scope,
    inputs: {},
    personas: {},
    personas_completed: [],
    rating: null,
    score: null,
    status: "running",
    ticket_ids: [],
    created_at: now,
    updated_at: now,
  };
}

export interface GuardianAnalysisPanelProps {
  audit: Audit | null;
  pendingAuditId?: string | null;
  scope: AuditScope;
  scopeLabels: ScopeLabelCache;
  investigating?: boolean;
  trackedInFlight?: boolean;
  personaTab: AuditPersonaTabId;
  onPersonaTabChange: (id: AuditPersonaTabId) => void;
}

/** Persona tabs + panel content — shown inline during investigations (no report wrapper). */
export function GuardianAnalysisPanel({
  audit,
  pendingAuditId,
  scope,
  scopeLabels,
  investigating = false,
  trackedInFlight = false,
  personaTab,
  onPersonaTabChange,
}: GuardianAnalysisPanelProps) {
  const displayAudit = useMemo(() => {
    if (audit) return audit;
    if (investigating && pendingAuditId) {
      return placeholderAudit(pendingAuditId, scope);
    }
    return null;
  }, [audit, investigating, pendingAuditId, scope]);

  if (!displayAudit) return null;

  const pending = isAuditPending(displayAudit, trackedInFlight || investigating);
  const tabProgress = {
    auditRerunning: pending,
    personaRerunAction: null,
    rerunAnchorTab: null,
  };
  const activeTabRunning =
    computeTabVisualState(displayAudit, personaTab, tabProgress) === "running";
  const synthesisRunning =
    computeTabVisualState(displayAudit, "synthesis", tabProgress) === "running";
  const entityScope = entityTextScopeFromAuditScope(displayAudit.scope);

  return (
    <div data-testid="guardian-analysis-panel">
      <AuditPersonaTabs
        audit={displayAudit}
        activeId={personaTab}
        onChange={onPersonaTabChange}
        progress={tabProgress}
      />
      <div className="mt-3">
        {personaTab === "synthesis" ? (
          synthesisRunning ? (
            <PersonaPanel
              result={null}
              label="Synthesis"
              running
              entityScope={entityScope}
              scopeLabels={scopeLabels}
            />
          ) : (
            <div className="text-sm leading-relaxed text-slate-300">
              <EntityRichText
                text={resolveSynthesisBody(displayAudit) || "No synthesis yet."}
                scope={entityScope}
                scopeLabels={scopeLabels}
              />
            </div>
          )
        ) : (
          <PersonaPanel
            result={displayAudit.personas[personaTab]}
            label={personaTab}
            running={activeTabRunning}
            entityScope={entityScope}
            scopeLabels={scopeLabels}
          />
        )}
      </div>
    </div>
  );
}
